import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useState } from 'react'
import { PrimaryButton, SecondaryButton } from '../../components/Button'
import PageHeader from '../../components/PageHeader'
import Table from '../../components/Table'
import AssetWithdrawalModal, { WithdrawableAsset } from '../../components/AssetWithdrawalModal'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import ExportBalanceForm from '../../components/ExportBalanceForm'
import LoadingBar from '../../components/LoadingBar'
import Modal from '../../components/Modal'
import Dots from '../../icons/Dots'
import Treasury from '../../state/Treasury'
import { RADOM_COLORS } from '../../util/Constants'
import { getMethod } from '../../util/Managed'
import { useTestMode, formatCurrency, errorToast, successToast } from '../../util/Util'
import { TokenTitle } from '../Funds'
import User from '../../state/User'
import PaymentMethodDisplay from '../../components/PaymentMethodDisplay'
import AssetConversionRFQModal from '../../components/AssetConversionRFQModal'
import Radom, { ConversionPair, IPaymentMethod } from '../../state/Radom'
import ConvertIcon from '../../icons/ConvertIcon'
import Tooltip from '../../components/Tooltip'

const isSamePaymentMethod = (a: IPaymentMethod, b: IPaymentMethod): boolean => {
  return a.network === b?.network && a.token === b?.token
}

const Overview = observer((): ReactElement => {
  const [testMode] = useTestMode()

  const [modalOpen, setModalOpen] = useState(false)
  const [exportBalanceModalOpen, setExportBalanceModalOpen] = useState(false)
  const [assetToWithdraw, setAssetToWithdraw] = useState<WithdrawableAsset>({
    maxAmount: '',
    network: 'Ethereum'
  })

  const [assetToConvertFrom, setAssetToConvertFrom] = useState<IPaymentMethod | null>(null)
  const [convertModalOpen, setConvertModalOpen] = useState(false)
  const [conversionPairs, setConversionPairs] = useState<ConversionPair[]>([])

  useEffect(() => {
    Treasury.loadBalances()

    const fetchConversionPairs = async (): Promise<void> => {
      try {
        const response = await Radom.getConversionPairs()
        setConversionPairs(response)
      } catch (err) {
        console.error('Failed to get conversion pairs', err.response?.data?.error || err.message)
      }
    }

    fetchConversionPairs()
  }, [])

  const onClickWithdraw = (w: WithdrawableAsset): void => {
    setAssetToWithdraw(w)
    setModalOpen(true)
  }

  const onOpenConvert = (w: WithdrawableAsset): void => {
    // TODO: unify these modals into a single hook
    setAssetToConvertFrom(w)
    setConvertModalOpen(true)
  }

  const possibleIntoAssets = conversionPairs
    .filter(({ from }) => assetToConvertFrom && isSamePaymentMethod(from, assetToConvertFrom))
    .map(({ into }) => into)

  return <>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <PageHeader title='Balances' subTitle='Manage your balances' />
      <Dropdown
        noChevron
        selectedContent={<Dots width={15} />}
        dropdownContent={
          <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
            <DropdownItem onClick={() => setExportBalanceModalOpen(true)}>Export balance report</DropdownItem>
          </div>
        }
      />
    </div>
    <Table
      borderless
      headers={['Asset', 'Balance', 'Exchange rate', ''].map(h => <span key={h}>{h}</span>)}
      items={
        Treasury.managedBalances
          .filter(b => Number(b.balance) > 0)
          .filter(b => {
            const m = getMethod(b.network, b.token)?.isTestnet
            return testMode ? m : !m
          })
          .sort((a, b) => {
            const conversionA = Treasury.priceQuotes
              .find(p => p.from.toLowerCase() === getMethod(a.network, a.token).ticker.toLowerCase())
            const conversionB = Treasury.priceQuotes
              .find(p => p.from.toLowerCase() === getMethod(b.network, b.token).ticker.toLowerCase())

            const aV = Number(a.balance) * (conversionA?.value || 1)
            const bV = Number(b.balance) * (conversionB?.value || 1)

            return bV - aV
          }).map(b => {
            const method = getMethod(b.network, b.token)
            if (b.network === 'Fiat') {
              method.currencyName = b.token
              method.isFiat = true
            }
            const conversion = Treasury.priceQuotes
              .find(p => p.from.toLowerCase() === method.ticker.toLowerCase())

            return [
              <div key="token" style={{ display: 'flex', flexDirection: 'column', fontSize: 14 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <PaymentMethodDisplay method={{ network: b.network, token: b.token }} excludeName size={30} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <TokenTitle>{method.currencyName ?? method.name}</TokenTitle>
                    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                      {
                        !method.isFiat && <span style={{ color: RADOM_COLORS.GRAY_DARKEST }}>
                          {method.ticker} {method.isFiat ? undefined : `on ${method.hostChain?.name ?? method.name}`}
                        </span>
                      }
                    </div>
                  </div>
                </div>
              </div>,
              <div key="value" style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {
                  new Intl.NumberFormat('en-US', {
                    maximumSignificantDigits: 18
                  }).format(b.balance as any)
                }
                {
                  conversion
                    ? <span style={{ opacity: 0.5 }}>
                      ≈ {formatCurrency(Number(b.balance) * (conversion.value), User.organization?.defaultCurrency ?? 'USD')}
                    </span>
                    : <LoadingBar style={{ height: 10 }} />
                }
              </div>,

              <div key='conversion'>
                {
                  conversion
                    ? <div style={{
                      color: RADOM_COLORS.BLACK,
                      borderRadius: 5,
                      padding: '0 5px'
                    }}>
                      {formatCurrency(Number(conversion.value), User.organization?.defaultCurrency ?? 'USD')} {User.organization?.defaultCurrency ?? 'USD'}
                    </div>
                    : <LoadingBar style={{ height: 16 }} />
                }
              </div>,

              <div key="withdraw" style={{ display: 'flex', gap: '8px' }}>
                {
                  !method.isFiat &&
                  <PrimaryButton
                    onClick={() => {
                      onClickWithdraw({
                        network: b.network,
                        token: b.token,
                        maxAmount: b.balance
                      })
                    }}
                    style={{
                      gap: 5
                    }}>
                    Withdraw
                  </PrimaryButton>
                }
                {
                  User.organization?.features?.isConversionEnabled &&
                  conversionPairs.map(({ from }) => from).find(x => isSamePaymentMethod(x, b)) && // check if there is an available conversion
                  <Tooltip message='Convert balance'>
                    <SecondaryButton
                      onClick={() => {
                        onOpenConvert({
                          network: b.network,
                          token: b.token,
                          maxAmount: b.balance
                        })
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        fontSize: 16,
                        padding: 12,
                        border: 0,
                        backgroundColor: RADOM_COLORS.GRAY_DARK
                      }}>
                      <ConvertIcon
                        stroke={RADOM_COLORS.NICE_BLUE}
                        strokeWidth={2.1}
                        style={{ width: 15, height: 15 }} />
                    </SecondaryButton>
                  </Tooltip>
                }
              </div>
            ]
          })
      }
      isLoading={Treasury.loading}
      emptyPlaceholderText="No funds yet"
    />

    {
      modalOpen &&
      <AssetWithdrawalModal
        modalOpen={modalOpen}
        onModalOpen={open => setModalOpen(open)}
        assetToWithdraw={assetToWithdraw}
      />
    }

    {
      exportBalanceModalOpen &&
      <Modal visible={exportBalanceModalOpen} onClose={() => setExportBalanceModalOpen(false)} title='Export balance report'>
        <ExportBalanceForm />
      </Modal>
    }

    {
      convertModalOpen && assetToConvertFrom && possibleIntoAssets &&
      <AssetConversionRFQModal
        modalOpen={convertModalOpen}
        onClose={() => setConvertModalOpen(false)}
        fromAsset={assetToConvertFrom}
        availableIntoAssets={possibleIntoAssets}
        selectedIntoAsset={possibleIntoAssets[0]}
        onConvert={async (quoteId) => {
          try {
            await Radom.acceptQuote(quoteId)
            successToast('Successfully converted assets')
            setConvertModalOpen(false)
            Treasury.loadBalances()
          } catch (err) {
            errorToast(err.response?.data?.error || err.message)
          }
        }}
      />
    }
  </>
})

export default Overview
